/**
 * @file FormComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de formularios de Next.js
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-04-09
 * @modified 2026-02-27
 * @author Correcta
 * @author Mohammed Derfoufi
 * @modified Sergio Manjón
 * @copyright © Correcta
 * @license Private
 * */

"use client";

import type { FormComponentProps } from "@/types/ui";
import Form from "next/form";
import TitleComponent from "@/components/ui/TitleComponent";
import ButtonComponent from "@/components/ui/ButtonComponent";
import type React from "react";
import { useTranslations } from "next-intl";
import { TYPE_BUTTON, BUTTON_STATUS } from "@/constants/button";
import SubmitButtonComponent from "./SubmitButtonComponent";
import { useLoadingOverlay } from "@/context/LoadingOverlayProvider";

interface HybridFormComponentProps<
  TFormData,
> extends FormComponentProps<TFormData> {
  useNativeForm?: boolean;
  handleRefresh?: () => void;
}

const FormComponent = <TFormData = undefined,>({
  title,
  formAction,
  formClassName,
  children,
  formData,
  buttonSubmit = false,
  buttonSubmitText = "",
  buttonClassName,
  submitIcon,
  submitIconPosition = "left",
  isButtonEnabled,
  refreshIcon,
  useNativeForm = false,
  needSubmitIcon,
  handleRefresh,
}: HybridFormComponentProps<TFormData>) => {
  const ariaLabelT = useTranslations("AriaLabel");
  const { show, hide } = useLoadingOverlay();

  const handleNativeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    if (formAction) {
      show("data");

      try {
        await Promise.resolve(formAction(formDataObj));
      } finally {
        hide("data");
      }
    }
  };

  if (useNativeForm) {
    return (
      <form onSubmit={handleNativeSubmit} className={formClassName}>
        {title && (
          <section
            className={
              formClassName ? formClassName + "__header" : "form__header"
            }
          >
            <TitleComponent title={title} />
          </section>
        )}

        <section
          className={
            formClassName ? formClassName + "__content" : "form__content"
          }
        >
          {children}
        </section>

        {((buttonSubmit && formData) || refreshIcon) && (
          <section
            className={
              formClassName ? formClassName + "__footer" : "form__footer"
            }
          >
            {buttonSubmit && formData && (
              <SubmitButtonComponent
                formData={formData}
                buttonLabel={buttonSubmitText}
                status="primary"
                iconPosition={submitIconPosition}
                className={buttonClassName}
                isButtonEnabled={isButtonEnabled}
              >
                {needSubmitIcon && submitIcon}
              </SubmitButtonComponent>
            )}
            {refreshIcon && (
              <ButtonComponent
                type={TYPE_BUTTON}
                status={BUTTON_STATUS.OUTLINE}
                onClick={handleRefresh}
                ariaLabel={ariaLabelT("refreshForm")}
              >
                {refreshIcon}
              </ButtonComponent>
            )}
          </section>
        )}
      </form>
    );
  }

  // Comportamiento original con next/form
  return (
    <Form action={formAction} className={formClassName}>
      {title && (
        <section
          className={
            formClassName ? formClassName + "__header" : "form__header"
          }
        >
          <TitleComponent title={title} />
        </section>
      )}

      <section
        className={
          formClassName ? formClassName + "__content" : "form__content"
        }
      >
        {children}
      </section>

      {((buttonSubmit && formData) || refreshIcon) && (
        <section
          className={
            formClassName ? formClassName + "__footer" : "form__footer"
          }
        >
          {buttonSubmit && formData && (
            <SubmitButtonComponent
              formData={formData}
              buttonLabel={buttonSubmitText}
              status="primary"
              iconPosition={submitIconPosition}
              className={buttonClassName}
              isButtonEnabled={isButtonEnabled}
            >
              {needSubmitIcon && submitIcon}
            </SubmitButtonComponent>
          )}
          {refreshIcon && (
            <ButtonComponent
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.OUTLINE}
              onClick={handleRefresh}
              ariaLabel={ariaLabelT("refreshForm")}
            >
              {refreshIcon}
            </ButtonComponent>
          )}
        </section>
      )}
    </Form>
  );
};

export default FormComponent;
